import React from "react";
import "./../css/Header.css";
import { useSnackbar } from 'notistack';
import Search from './Search';
import Import from './Import';

type ViewType = 'MONTH' | 'WEEK' | 'DAY';

interface HeaderProps {
  currentView: ViewType;
  setCurrentView: (view: ViewType) => void;
  data: any[];
  onEventClick: (event: any) => void;
  onImportSuccess: () => void;
  pushEnabled: boolean;
  setPushEnabled: (enabled: boolean) => void;
  showSchulferien: boolean;
  setShowSchulferien: (show: boolean) => void;
}

interface WeatherData {
  temp: number;
  description: string;
  icon: string;
}

function Header({ currentView, setCurrentView, data, onEventClick, onImportSuccess, pushEnabled, setPushEnabled, showSchulferien, setShowSchulferien }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isImportOpen, setIsImportOpen] = React.useState(false);
  const [weather, setWeather] = React.useState<WeatherData | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const VAPID_PUBLIC_KEY = "BHQfVmwaR-RLH8CEMM1Skgy26Q_Kn7_WY-Yd5yK_iP2MHHlG8zvmqAJz7fAzUNlbvk-tl4QgKxURGJ_V0uOyTZc";

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
      await fetch("https://norbertspringer.de/calendar/api/saveSubscription.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscription: subscription.toJSON(), idUser: 1 }),
      });
      setPushEnabled(true);
      enqueueSnackbar("Push-Benachrichtigungen aktiviert", { variant: "success" });
    } catch (error) {
      console.error("Push subscription failed:", error);
      enqueueSnackbar("Push-Benachrichtigungen konnten nicht aktiviert werden", { variant: "error" });
    }
  };

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      enqueueSnackbar("Browser unterstützt keine Benachrichtigungen", { variant: "error" });
      return;
    }
    if (Notification.permission === "granted") {
      subscribeToPush();
    } else if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        subscribeToPush();
      }
    }
  };

  React.useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.ready.then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription();
        setPushEnabled(!!subscription);
      });
    }
  }, [setPushEnabled]);

  React.useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Verwende Open-Meteo API (keine API-Key erforderlich)
        const response = await fetch(
          'https://api.open-meteo.com/v1/forecast?latitude=50.8333&longitude=12.9167&current=temperature_2m,weather_code&timezone=Europe/Berlin'
        );
        const data = await response.json();
        
        // Wetter-Code zu Icon und Beschreibung
        const weatherCodes: { [key: number]: { icon: string; description: string } } = {
          0: { icon: '☀️', description: 'Klar' },
          1: { icon: '🌤️', description: 'Überwiegend klar' },
          2: { icon: '⛅', description: 'Teilweise bewölkt' },
          3: { icon: '☁️', description: 'Bewölkt' },
          45: { icon: '🌫️', description: 'Neblig' },
          48: { icon: '🌫️', description: 'Neblig' },
          51: { icon: '🌦️', description: 'Leichter Nieselregen' },
          53: { icon: '🌦️', description: 'Nieselregen' },
          55: { icon: '🌧️', description: 'Starker Nieselregen' },
          61: { icon: '🌧️', description: 'Leichter Regen' },
          63: { icon: '🌧️', description: 'Regen' },
          65: { icon: '🌧️', description: 'Starker Regen' },
          71: { icon: '🌨️', description: 'Leichter Schneefall' },
          73: { icon: '🌨️', description: 'Schneefall' },
          75: { icon: '🌨️', description: 'Starker Schneefall' },
          77: { icon: '🌨️', description: 'Schneegriesel' },
          80: { icon: '🌦️', description: 'Leichte Schauer' },
          81: { icon: '🌧️', description: 'Schauer' },
          82: { icon: '⛈️', description: 'Starke Schauer' },
          85: { icon: '🌨️', description: 'Leichte Schneeschauer' },
          86: { icon: '🌨️', description: 'Schneeschauer' },
          95: { icon: '⛈️', description: 'Gewitter' },
          96: { icon: '⛈️', description: 'Gewitter mit Hagel' },
          99: { icon: '⛈️', description: 'Gewitter mit Hagel' },
        };

        const weatherCode = data.current.weather_code;
        const weatherInfo = weatherCodes[weatherCode] || { icon: '🌡️', description: 'Wetter' };

        setWeather({
          temp: Math.round(data.current.temperature_2m),
          description: weatherInfo.description,
          icon: weatherInfo.icon,
        });
      } catch (error) {
        console.error('Fehler beim Laden des Wetters:', error);
      }
    };

    fetchWeather();
    // Aktualisiere das Wetter alle 30 Minuten
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleViewChange = (newView: ViewType) => {
    setCurrentView(newView);
    localStorage.setItem('calendarView', newView);
    
    const viewNames = { MONTH: 'Monatsansicht', WEEK: 'Wochenansicht', DAY: 'Tagesansicht' };
    enqueueSnackbar(`${viewNames[newView]}`, { variant: 'info', autoHideDuration: 1500 });
    setIsMenuOpen(false);
  };

  const handleViewSearch = () => {
    setIsSearchOpen(true);
    setIsMenuOpen(false);
  };

  return (
    <header>
      <div className="header-content">
        <h1>Unsere Terminübersicht</h1>
        {weather && (
          <div className="weather-info">
            <span className="weather-icon">{weather.icon}</span>
            <span className="weather-temp">{weather.temp}°C</span>
            <span className="weather-description">{weather.description}</span>
          </div>
        )}
      </div>
      
      {/* Hamburger Menu - nur auf Handy sichtbar */}
      <button
        className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Menu"
      >
        <span />
        <span />
        <span />
      </button>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <div
          className="menu-overlay"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Menu Content */}
      <nav className={`menu-nav ${isMenuOpen ? 'open' : ''}`}>
        <ul>
          <li className="menu-section-title">Ansicht</li>
          <li>
            <button
              className={`view-button ${currentView === 'MONTH' ? 'active' : ''}`}
              onClick={() => handleViewChange('MONTH')}
            >
              📅 Monatsansicht
            </button>
          </li>
          <li>
            <button
              className={`view-button ${currentView === 'WEEK' ? 'active' : ''}`}
              onClick={() => handleViewChange('WEEK')}
            >
              📆 Wochenansicht
            </button>
          </li>
          <li>
            <button
              className={`view-button ${currentView === 'DAY' ? 'active' : ''}`}
              onClick={() => handleViewChange('DAY')}
            >
              📋 Tagesansicht
            </button>
          </li>
          <li>
            <button
              className="view-button" 
              onClick={() => handleViewSearch()}
            >
              🔍 Suchen
            </button>
          </li>
          <li>
            <button
              className="view-button" 
              onClick={() => {
                setIsImportOpen(true);
                setIsMenuOpen(false);
              }}
            >
              📥 Importieren
            </button>
          </li>
          <li>
            <button
              className={`view-button ${showSchulferien ? 'active' : ''}`}
              onClick={() => {
                const newValue = !showSchulferien;
                setShowSchulferien(newValue);
                localStorage.setItem('showSchulferien', String(newValue));
                enqueueSnackbar(
                  showSchulferien ? 'Schulferien ausgeblendet' : 'Schulferien eingeblendet',
                  { variant: 'info', autoHideDuration: 1500 }
                );
                setIsMenuOpen(false);
              }}
            >
              🎒 {showSchulferien ? 'Ferien ausblenden' : 'Ferien anzeigen'}
            </button>
          </li>
          {process.env.NODE_ENV === "production" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window && (
            <li>
              <button
                className={`view-button ${pushEnabled ? 'active' : ''}`}
                onClick={() => {
                  if (!pushEnabled) {
                    requestNotificationPermission();
                    setIsMenuOpen(false);
                  }
                }}
                disabled={pushEnabled}
              >
                🔔 {pushEnabled ? 'Push aktiviert' : 'Push aktivieren'}
              </button>
            </li>
          )}
        </ul>
      </nav>

      {/* Search Dialog */}
      <Search 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)}
        data={data}
        onEventClick={onEventClick}
      />

      {/* Import Dialog */}
      <Import
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImportSuccess={onImportSuccess}
      />
    </header>
  );
}
export default Header;
